import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Button, Typography, Space, Tag, Modal, Form, Input, message, Alert, Tooltip,
} from 'antd';
import { ReloadOutlined, UserAddOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { adminService, type GuestCustomer } from '../../services/adminService';

const { Title, Text } = Typography;

interface GuestCustomersProps {
  /** Rendered inside a tab — drop the outer padding/title. */
  embedded?: boolean;
}

const GuestCustomers: React.FC<GuestCustomersProps> = ({ embedded = false }) => {
  const [guests, setGuests] = useState<GuestCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [converting, setConverting] = useState<GuestCustomer | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      setGuests(await adminService.getGuestCustomers());
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to load guest customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openConvert = (g: GuestCustomer) => {
    setConverting(g);
    form.resetFields();
    form.setFieldsValue({ email: g.email, name: g.name || '' });
  };

  const handleConvert = async () => {
    let values: any;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const r = await adminService.convertGuest({
        email: values.email,
        name: values.name,
        traderCode: values.traderCode || undefined,
      });
      // The account exists either way, but if the email didn't go out the
      // customer has no idea — that needs to read as a warning, not a success.
      if (r.emailSent) {
        message.success(r.message);
      } else {
        message.warning(r.message);
      }
      setConverting(null);
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to convert guest');
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? guests.filter((g) => g.email.toLowerCase().includes(q) || g.name.toLowerCase().includes(q)) : guests;
  }, [guests, search]);

  const columns = [
    {
      title: 'Guest', key: 'guest',
      render: (_: any, g: GuestCustomer) => (
        <Space direction="vertical" size={0}>
          <Text strong>{g.email}</Text>
          {g.name ? <Text type="secondary" style={{ fontSize: 12 }}>{g.name}</Text> : null}
        </Space>
      ),
    },
    { title: 'Orders', dataIndex: 'orderCount', key: 'orderCount', align: 'right' as const,
      render: (v: number) => <Tag color="blue">{v}</Tag> },
    { title: 'Total', dataIndex: 'totalAmount', key: 'totalAmount', align: 'right' as const,
      render: (v: number) => `$${(Number(v) || 0).toFixed(2)}` },
    { title: 'Last Order', dataIndex: 'lastOrderDate', key: 'lastOrderDate',
      render: (v: string | null) => v ? dayjs(v).format('DD MMM YYYY') : '—' },
    {
      title: 'Action', key: 'action', width: 160,
      render: (_: any, g: GuestCustomer) => (
        <Button size="small" type="primary" ghost icon={<UserAddOutlined />} onClick={() => openConvert(g)}>
          Convert
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: embedded ? 0 : 24 }}>
      <Space style={{ width: '100%', justifyContent: embedded ? 'flex-end' : 'space-between', marginBottom: 16 }} wrap>
        {!embedded && <Title level={3} style={{ margin: 0 }}>Guest Customers</Title>}
        <Space>
          <Input.Search allowClear placeholder="Search email or name…" style={{ maxWidth: 280 }}
            onChange={(e) => setSearch(e.target.value)} />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        </Space>
      </Space>

      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="Customers who ordered as a guest"
        description="These emails placed orders without an account. Converting one creates a verified account (share the password with them) and links all their guest orders. Emails that already have an account are not shown here."
      />

      <Card>
        <Table
          rowKey="email"
          dataSource={filtered}
          columns={columns}
          loading={loading}
          size="middle"
          scroll={{ x: 640 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} guest customers` }}
          locale={{ emptyText: 'No guest customers.' }}
        />
      </Card>

      <Modal
        title={`Convert guest — ${converting?.email ?? ''}`}
        open={!!converting}
        onCancel={() => setConverting(null)}
        onOk={handleConvert}
        okText="Create account & link orders"
        confirmLoading={saving}
        destroyOnHidden
      >
        {converting && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`This will create an account and link ${converting.orderCount} order(s) to it.`}
            description="The customer is emailed a link to set their own password, so there's no password for you to choose or pass on. The link lasts 7 days; after that they can use “Forgot password”."
          />
        )}
        <Form form={form} layout="vertical">
          <Form.Item name="email" label="Email (login ID)"
            rules={[{ required: true, type: 'email' }, { max: 50, message: '50 characters or less' }]}>
            <Input disabled />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true, max: 100 }]}>
            <Input placeholder="Customer name" />
          </Form.Item>
          <Form.Item name="traderCode" label="Trader Code (optional)"
            tooltip="Link to the ERP customer master, if this is a trade account.">
            <Input placeholder="e.g. TC001" maxLength={20} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GuestCustomers;
